import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import ProductImageManager from '@/components/ProductImageManager';
import { useSession } from '@/context/SessionContext';
import RichTextEditor from '@/components/RichTextEditor';
import LocationInput from '@/components/LocationInput';
import CategorySelector from '@/components/CategorySelector';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { X } from '@/components/icons';

interface NoteFormModernProps {
  onSubmit: (note: {
    title: string;
    content: string;
    images: string[];
    mainImage: string | null;
    location: any;
    categoryId: string | null;
  }) => void;
  loading?: boolean;
}

const NoteFormModern: React.FC<NoteFormModernProps> = ({ onSubmit, loading }) => {
  const { toast } = useToast();
  const { session, profile } = useSession();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const imagesRef = useRef<string[]>([]);
  const mainImageRef = useRef<string | null>(null);
  // Set default location to user's relevant city if available
  const [location, setLocation] = useState(() =>
    profile?.relevant_location_display_name && profile?.relevant_latitude != null && profile?.relevant_longitude != null
      ? {
          displayName: profile.relevant_location_display_name,
          latitude: profile.relevant_latitude,
          longitude: profile.relevant_longitude,
        }
      : null
  );
  const [showLocationEditor, setShowLocationEditor] = useState(false);
  const [editingLocation, setEditingLocation] = useState<NoteLocation | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const handleSubmit = () => {
    if (!title.trim()) {
      toast({ title: 'Error', description: 'El título es obligatorio.', variant: 'destructive' });
      return;
    }
    if (!content.trim() || content === '<p></p>') {
      setConfirmationMessage('El contenido está vacío. ¿Deseas publicar de todos modos?');
      setShowConfirmationDialog(true);
      return;
    }
    // Aquí iría la lógica de publicación
    onSubmit({
      title,
      content,
      images: imagesRef.current,
      mainImage: mainImageRef.current,
      location,
      categoryId,
    });
  };

  const handleConfirmPublish = () => {
    setShowConfirmationDialog(false);
    // Aquí iría la lógica de publicación real
    onSubmit({
      title,
      content,
      images: imagesRef.current,
      mainImage: mainImageRef.current,
      location,
      categoryId,
    });
  };

  const handleClear = () => {
    setTitle('');
    setContent('');
    imagesRef.current = [];
    mainImageRef.current = null;
    setLocation(null);
    setCategoryId(null);
    toast({ title: 'Formulario limpiado', description: 'El formulario ha sido limpiado.' });
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Crear Nueva Nota</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2">
            <Label htmlFor="note-title">Título</Label>
            <Input id="note-title" value={title} onChange={e => setTitle(e.target.value)} disabled={loading} />
          </div>
          <CategorySelector selectedCategoryId={categoryId} onCategoryChange={setCategoryId} disabled={loading} />
          <div className="grid gap-2">
            <Label>Imágenes</Label>
            <ProductImageManager
              session={session}
              initialImageUrls={[]}
              initialMainImageUrl={null}
              onImagesChange={(allImageUrls, mainImageUrl) => {
                imagesRef.current = allImageUrls;
                mainImageRef.current = mainImageUrl;
              }}
              disabled={loading}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="note-content">Contenido</Label>
            <RichTextEditor content={content} onChange={setContent} placeholder="¿Qué tenés en mente?" session={null} disabled={loading} />
          </div>
          <div className="grid gap-2 p-3 bg-muted rounded-md">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <Label className="text-sm font-medium">Ubicación</Label>
                {!showLocationEditor ? (
                  <button
                    type="button"
                    className="w-full text-left text-sm text-muted-foreground mt-1 px-2 py-1 border border-transparent rounded-md hover:bg-muted"
                    onClick={() => {
                      setEditingLocation(null);
                      setShowLocationEditor(true);
                    }}
                  >
                    {location?.displayName || 'Ubicación no establecida'}
                  </button>
                ) : null}
              </div>
              <div className="ml-2">
                {!showLocationEditor ? (
                  <Button size="sm" variant="ghost" onClick={() => { setShowLocationEditor(true); setEditingLocation(null); }}>Editar</Button>
                ) : (
                  <div className="flex space-x-2">
                    <Button size="sm" variant="ghost" onClick={() => { setShowLocationEditor(false); }}>Cerrar</Button>
                  </div>
                )}
              </div>
            </div>
            {showLocationEditor && (
              <div className="mt-2 flex items-start space-x-2">
                <div className="flex-1">
                  <LocationInput
                    autoFocus
                    showLabel={false}
                    value={editingLocation || location}
                    onChange={loc => setEditingLocation(loc)}
                    disabled={loading}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Button size="sm" onClick={() => {
                    if (editingLocation && editingLocation.displayName) {
                      setLocation(editingLocation);
                    }
                    setShowLocationEditor(false);
                  }}>Guardar</Button>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleSubmit} disabled={loading || !title.trim()} className="flex-1">Publicar Nota</Button>
            <Button variant="outline" onClick={handleClear} disabled={loading} className="flex items-center gap-2">
              <X className="h-3 w-3 sm:h-4 sm:w-4" />
              Limpiar Formulario
            </Button>
          </div>
        </CardContent>
        <AlertDialog open={showConfirmationDialog} onOpenChange={setShowConfirmationDialog}>
          <AlertDialogContent aria-labelledby="confirm-publish-title" aria-describedby="confirm-publish-description">
            <AlertDialogHeader>
              <AlertDialogTitle id="confirm-publish-title">Confirmar Publicación</AlertDialogTitle>
              <AlertDialogDescription id="confirm-publish-description">{confirmationMessage}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmPublish}>Publicar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
};

export default NoteFormModern;
